import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { EVENT_CREDITS_PER_IMPORT } from "../shared/credits";
import type { CrawlerEntitlementStatus } from "../shared/entitlements";
import { CreditBalance } from "./CreditBalance";
import { RunResultsDialog } from "./RunResults";
import { api, ApiError, downloadFile } from "./api";
import { login, logout } from "./auth";

interface ClientAccount {
  id: string;
  name: string | null;
  role: "admin" | "viewer";
  canCreateJobs: boolean;
  canManageBilling: boolean;
}

interface User {
  subject: string;
  email: string | null;
  displayName: string | null;
  clients: ClientAccount[];
  entitlementsMode: "enforce" | "bypass";
  scheduledDelegations: Array<{
    id: string;
    clientId: string;
    status: string;
    createdAt: string;
    lastUsedAt: string | null;
    jobCount: number;
  }>;
}

interface Job {
  id: string;
  owner_sub: string;
  name: string;
  client_id: string;
  seed_url: string;
  entity_kind: "event";
  crawl_mode: "single" | "collection";
  cadence: "once" | "daily" | "weekly" | "monthly";
  timezone: string;
  local_time: string;
  status: string;
  authorization_error: string | null;
  next_run_at: string | null;
  active_run_id: string | null;
  last_run_status: string | null;
  last_run_error_code: string | null;
  created_at: string;
}

interface Run {
  id: string;
  trigger_type: string;
  retry_of_run_id: string | null;
  status: string;
  discovered_count: number;
  succeeded_count: number;
  failed_count: number;
  rejected_count: number;
  unchanged_count: number;
  removed_count: number;
  credits_settled: number;
  credits_released: number;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  errors: Array<{ code: string; message: string; count: number }>;
  stages: Array<{
    key: "discovery" | "scrape" | "structure" | "ingest";
    label: string;
    status: string;
    total: number;
    completed: number;
    failed: number;
    skipped: number;
    reused: number;
  }>;
}

type RunStageKey = Run["stages"][number]["key"];

interface RunItemSummary {
  id: string;
  sourceKey: string;
  sourceUrl: string;
  canonicalUrl: string;
  occurrenceOrder: number;
  status: string;
  attemptCount: number;
  hasScrapeData: boolean;
  hasExtractionTrace: boolean;
  hasStructuredData: boolean;
  normalizedSchemaVersion: string | null;
  clientEntityId: string | null;
  clientResultStatus: string | null;
  clientRequestId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  firstSeenAt: string;
  completedAt: string | null;
}

interface RunItemDetail {
  item: RunItemSummary;
  scrapeEvidence: unknown;
  extractionTrace: unknown;
  structuredData: unknown;
  importResults: unknown;
  importAttempts: unknown[];
}

interface DiscoveryPage {
  page: number;
  artifactKey: string;
  finalUrl: string | null;
  canonicalUrl: string | null;
  title: string | null;
  linkCount: number;
  hasEvidence: boolean;
}

interface RunInspection {
  run: Run;
  stage: RunStageKey;
  items: RunItemSummary[];
  artifactRetentionDays: number;
  discoveryPages: DiscoveryPage[];
}

interface JobForm {
  name: string;
  seedUrl: string;
  entityKind: "event";
  crawlMode: "single" | "collection";
  cadence: "once" | "daily" | "weekly" | "monthly";
  timezone: string;
  localTime: string;
  weekday: number;
  monthday: number;
  maxListPages: number;
  maxEntities: number;
}

const defaultForm: JobForm = {
  name: "",
  seedUrl: "",
  entityKind: "event",
  crawlMode: "collection",
  cadence: "once",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  localTime: "06:00",
  weekday: 1,
  monthday: 1,
  maxListPages: 50,
  maxEntities: 1000,
};

const BILLING_URL = "https://billing.future-demand.com";
const cadenceLabels: Record<JobForm["cadence"], string> = {
  once: "One-time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

type LoginLanguage = "en" | "de";

const loginCopy = {
  en: {
    badge: "Secure sign-in",
    eyebrow: "Welcome back",
    title: "Sign in to Future Demand",
    intro: "Enter your work email. We’ll take you to the same secure Future Demand sign-in used by the billing portal.",
    emailLabel: "Work email",
    emailHint: "The email associated with your Future Demand account.",
    emailPlaceholder: "you@company.com",
    continue: "Continue",
    continuing: "Checking…",
    errorTitle: "Couldn’t sign you in",
    error: "Something went wrong. Please try again.",
  },
  de: {
    badge: "Sichere Anmeldung",
    eyebrow: "Willkommen zurück",
    title: "Bei Future Demand anmelden",
    intro: "Gib deine geschäftliche E-Mail-Adresse ein. Wir leiten dich zur sicheren Future Demand Anmeldung des Abrechnungsportals weiter.",
    emailLabel: "Geschäftliche E-Mail-Adresse",
    emailHint: "Die E-Mail-Adresse deines Future Demand Kontos.",
    emailPlaceholder: "du@unternehmen.de",
    continue: "Weiter",
    continuing: "Wird geprüft…",
    errorTitle: "Anmeldung nicht möglich",
    error: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
  },
} satisfies Record<LoginLanguage, Record<string, string>>;

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function prettyJson(value: unknown): string {
  return value == null ? "No retained data for this step." : JSON.stringify(value, null, 2);
}

const stageTitles: Record<RunStageKey, string> = {
  discovery: "Identified event pages",
  scrape: "Scraped page evidence",
  structure: "AI request, response, and structured event",
  ingest: "Lookup, create/update requests, and responses",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  authorization_required: "Authorization required",
  cancelled: "Cancelled",
  completed: "Completed",
  completed_with_issues: "Completed with issues",
  discovering: "Discovering",
  failed: "Failed",
  insufficient_credits: "Insufficient credits",
  awaiting_credits: "Checking credits",
  cancelling: "Stopping",
  paused: "Paused",
  pending: "Pending",
  processing: "Processing",
  queued: "Queued",
  running: "Running",
};

function Status({
  value,
  hasFailures = false,
  className = "",
}: {
  value: string;
  hasFailures?: boolean;
  className?: string;
}) {
  const displayValue = value === "completed" && hasFailures ? "completed_with_issues" : value;
  return (
    <span className={`status status-${displayValue.replaceAll("_", "-")} ${className}`.trim()}>
      {statusLabels[displayValue] ?? displayValue.replaceAll("_", " ")}
    </span>
  );
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [resultsRun, setResultsRun] = useState<Run | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [creditAcknowledged, setCreditAcknowledged] = useState(false);
  const [termsAcceptanceByClient, setTermsAcceptanceByClient] = useState<Record<string, boolean>>({});
  const [creditStatus, setCreditStatus] = useState<{ clientId: string; status: CrawlerEntitlementStatus | null; loading: boolean; error: boolean } | null>(null);
  const [creditRefresh, setCreditRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginLanguage, setLoginLanguage] = useState<LoginLanguage>(() => {
    const saved = localStorage.getItem("fd.lang");
    if (saved === "de" || saved === "en") return saved;
    return navigator.language.toLowerCase().startsWith("de") ? "de" : "en";
  });
  const [pendingActions, setPendingActions] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [drawerNotice, setDrawerNotice] = useState<string | null>(null);
  const [inspection, setInspection] = useState<RunInspection | null>(null);
  const [inspectionItem, setInspectionItem] = useState<RunItemDetail | null>(null);
  const [inspectionDiscovery, setInspectionDiscovery] = useState<unknown>(null);
  const [inspectionSelection, setInspectionSelection] = useState<string | null>(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const createJobPopover = useRef<HTMLFormElement>(null);

  function setActionPending(action: string, pending: boolean) {
    setPendingActions((current) => {
      const next = new Set(current);
      if (pending) next.add(action);
      else next.delete(action);
      return next;
    });
  }

  function actionPending(action: string): boolean {
    return pendingActions.has(action);
  }

  const load = useCallback(async () => {
    try {
      const me = await api<User>("/api/me");
      const jobResult = await api<{ jobs: Job[] }>("/api/jobs");
      setUser(me);
      setJobs(jobResult.jobs);
      setSelectedClient((current) => current || me.clients[0]?.id || "");
      setError(null);
    } catch (caught) {
      if (!(caught instanceof ApiError && caught.status === 401)) setError(caught instanceof Error ? caught.message : "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.documentElement.lang = loginLanguage;
    localStorage.setItem("fd.lang", loginLanguage);
  }, [loginLanguage]);

  useEffect(() => {
    if (!user || !selectedClient) return;
    let active = true;
    let pending = false;
    async function refresh() {
      if (pending) return;
      pending = true;
      setCreditStatus({ clientId: selectedClient, status: null, loading: true, error: false });
      try {
        const status = await api<CrawlerEntitlementStatus>(`/api/entitlements/status?clientId=${encodeURIComponent(selectedClient)}`);
        if (!active) return;
        if (status.clientId !== selectedClient) throw new Error("Credit account mismatch");
        setCreditStatus({ clientId: selectedClient, status, loading: false, error: false });
        setTermsAcceptanceByClient((current) => ({ ...current, [selectedClient]: status.termsAcceptanceRequired }));
      } catch (caught) {
        if (!active) return;
        setCreditStatus({ clientId: selectedClient, status: null, loading: false, error: true });
        if (caught instanceof ApiError && caught.code === "v4_acceptance_required") {
          setTermsAcceptanceByClient((current) => ({ ...current, [selectedClient]: true }));
        }
      } finally {
        pending = false;
      }
    }
    const refreshWhenVisible = () => { if (!document.hidden) void refresh(); };
    void refresh();
    const timer = window.setInterval(refreshWhenVisible, 30_000);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [selectedClient, user, creditRefresh, selectedJob?.active_run_id]);

  const selectedAccount = user?.clients.find((client) => client.id === selectedClient);
  const selectedDelegation = user?.scheduledDelegations.find(
    (delegation) => delegation.clientId === selectedClient,
  );
  const canManageJob = (job: Job) => selectedAccount?.role === "admin" || job.owner_sub === user?.subject;
  const visibleJobs = useMemo(() => jobs.filter((job) => job.client_id === selectedClient), [jobs, selectedClient]);
  const creditsUnavailable = visibleJobs.some(
    (job) => job.last_run_status === "insufficient_credits",
  );
  const runRequiresTermsAcceptance =
    visibleJobs.some((job) => job.last_run_error_code === "v4_acceptance_required") ||
    selectedJob?.last_run_error_code === "v4_acceptance_required";
  const showTermsAcceptanceNotice =
    termsAcceptanceByClient[selectedClient] ?? runRequiresTermsAcceptance;

  async function createJob(event: FormEvent) {
    event.preventDefault();
    setActionPending("create", true);
    setCreateError(null);
    try {
      await api("/api/jobs", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          clientId: selectedClient,
          weekday: form.cadence === "weekly" ? form.weekday : null,
          monthday: form.cadence === "monthly" ? form.monthday : null,
        }),
      });
      setForm(defaultForm);
      setCreditAcknowledged(false);
      createJobPopover.current?.hidePopover();
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "v4_acceptance_required") {
        setTermsAcceptanceByClient((current) => ({ ...current, [selectedClient]: true }));
      }
      setCreateError(caught instanceof Error ? caught.message : "Job could not be created");
    } finally {
      setActionPending("create", false);
    }
  }

  async function beginLogin(event: FormEvent) {
    event.preventDefault();
    setActionPending("login", true);
    setError(null);
    try {
      await login("/", loginEmail.trim());
    } catch {
      setError(loginCopy[loginLanguage].error);
      setActionPending("login", false);
    }
  }

  async function beginLogout() {
    setActionPending("logout", true);
    setError(null);
    try {
      await logout();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-out is unavailable");
      setActionPending("logout", false);
    }
  }

  async function jobAction(job: Job, action: "pause" | "resume" | "run" | "preview" | "refresh") {
    if (action === "refresh" && !window.confirm(`Refresh event details for every discovered event page, including unchanged pages? This re-extracts and imports event data and may update existing events. Each successfully imported event page costs ${EVENT_CREDITS_PER_IMPORT} credits, even if its source has not changed. Unsuccessful imports are free.`)) return;
    if (
      action === "run" &&
      !window.confirm(
        `Start this run now? Each new or changed event successfully imported into the client account costs ${EVENT_CREDITS_PER_IMPORT} credits. Unchanged and unsuccessful imports are free. A collection can import and charge for multiple events in one run.`,
      )
    ) {
      return;
    }
    const actionKey = `${job.id}:${action}`;
    setActionPending(actionKey, true);
    setError(null);
    setDrawerError(null);
    try {
      await api(`/api/jobs/${job.id}/${action}`, { method: "POST" });
      await load();
      if (selectedJob?.id === job.id) await openJob(job.id);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Action failed";
      if (selectedJob?.id === job.id) setDrawerError(message);
      else setError(message);
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function reauthorizeJob(job: Job) {
    const actionKey = `${job.id}:reauthorize`;
    setActionPending(actionKey, true);
    setError(null);
    setDrawerError(null);
    try {
      await api(`/api/jobs/${job.id}/reauthorize`, { method: "POST" });
      await load();
      if (selectedJob?.id === job.id) await openJob(job.id);
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Scheduled access could not be authorized";
      if (selectedJob?.id === job.id) setDrawerError(message);
      else setError(message);
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function revokeScheduledAccess(delegationId: string) {
    if (
      !window.confirm(
        "Revoke scheduled access for this account? Its crawler jobs will pause until an administrator authorizes them again.",
      )
    ) {
      return;
    }
    const actionKey = `delegation:${delegationId}:revoke`;
    setActionPending(actionKey, true);
    setError(null);
    try {
      await api(`/api/delegations/${delegationId}/revoke`, { method: "POST" });
      await load();
      if (selectedJob) await openJob(selectedJob.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Scheduled access could not be revoked");
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function openJob(id: string) {
    const actionKey = `${id}:details`;
    setActionPending(actionKey, true);
    try {
      const result = await api<{ job: Job; runs: Run[] }>(`/api/jobs/${id}`);
      setSelectedJob(result.job);
      setRuns(result.runs);
      setDrawerError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Job details could not be loaded");
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function retryFailedSteps(run: Run) {
    if (!selectedJob) return;
    if (
      !window.confirm(
        `Retry the failed steps? Each event successfully imported into the client account costs ${EVENT_CREDITS_PER_IMPORT} credits. Steps that remain unsuccessful consume no credits.`,
      )
    ) {
      return;
    }
    const actionKey = `${run.id}:retry`;
    setActionPending(actionKey, true);
    setDrawerError(null);
    try {
      await api(`/api/runs/${run.id}/retry`, { method: "POST" });
      await load();
      await openJob(selectedJob.id);
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "Failed steps could not be retried");
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function stopRun(run: Run) {
    if (!selectedJob) return;
    const actionKey = `${run.id}:stop`;
    setActionPending(actionKey, true);
    setDrawerError(null);
    setDrawerNotice(null);
    try {
      await api(`/api/runs/${run.id}/cancel`, { method: "POST" });
      setDrawerNotice("Stop requested. The current page will finish safely; no further pages will be processed or imported.");
      await load();
      await openJob(selectedJob.id);
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "The run could not be stopped");
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function downloadRunSummary(run: Run) {
    const actionKey = `${run.id}:summary`;
    setActionPending(actionKey, true);
    setDrawerError(null);
    try {
      const file = await downloadFile(`/api/runs/${run.id}/summary.csv`);
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "The run summary could not be downloaded");
    } finally {
      setActionPending(actionKey, false);
    }
  }

  async function loadInspectionItem(runId: string, itemId: string) {
    setInspectionLoading(true);
    setInspectionSelection(itemId);
    try {
      const detail = await api<RunItemDetail>(`/api/runs/${runId}/items/${itemId}`);
      setInspectionItem(detail);
      setInspectionDiscovery(null);
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "The retained stage data could not be loaded");
    } finally {
      setInspectionLoading(false);
    }
  }

  async function loadDiscoveryPage(runId: string, page: number) {
    setInspectionLoading(true);
    setInspectionSelection(`page:${page}`);
    try {
      const detail = await api<{ page: number; evidence: unknown }>(`/api/runs/${runId}/discovery/${page}`);
      setInspectionDiscovery(detail.evidence);
      setInspectionItem(null);
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "The retained discovery data could not be loaded");
    } finally {
      setInspectionLoading(false);
    }
  }

  async function openStageData(run: Run, stage: RunStageKey) {
    setInspectionLoading(true);
    setDrawerError(null);
    setInspectionItem(null);
    setInspectionDiscovery(null);
    setInspectionSelection(null);
    try {
      const [detail, discovery] = await Promise.all([
        api<{
          run: Run;
          items: RunItemSummary[];
          artifactRetentionDays: number;
        }>(`/api/runs/${run.id}`),
        stage === "discovery"
          ? api<{ pages: DiscoveryPage[] }>(`/api/runs/${run.id}/discovery`)
          : Promise.resolve({ pages: [] as DiscoveryPage[] }),
      ]);
      const nextInspection: RunInspection = {
        run,
        stage,
        items: detail.items,
        artifactRetentionDays: detail.artifactRetentionDays,
        discoveryPages: discovery.pages,
      };
      setInspection(nextInspection);
      if (stage === "discovery" && discovery.pages[0]) {
        await loadDiscoveryPage(run.id, discovery.pages[0].page);
      } else if (detail.items[0]) {
        await loadInspectionItem(run.id, detail.items[0].id);
      }
    } catch (caught) {
      setDrawerError(caught instanceof Error ? caught.message : "The retained stage data could not be loaded");
    } finally {
      setInspectionLoading(false);
    }
  }

  useEffect(() => {
    const jobId = selectedJob?.id;
    if (!jobId || !selectedJob.active_run_id) return;
    const timer = window.setInterval(() => {
      void api<{ job: Job; runs: Run[] }>(`/api/jobs/${jobId}`)
        .then((result) => {
          setSelectedJob(result.job);
          setRuns(result.runs);
        })
        .catch(() => undefined);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [selectedJob?.active_run_id, selectedJob?.id]);

  if (loading) {
    return (
      <main className="center">
        <div className="loader" />
        <p>Loading crawler workspace…</p>
      </main>
    );
  }
  if (!user) {
    const copy = loginCopy[loginLanguage];
    const validEmail = /.+@.+\..+/u.test(loginEmail.trim());
    return (
      <div className="fd-public">
        <header className="fd-public__bar">
          <div className="fd-public__bar-inner">
            <a className="fd-public__brand" href="https://future-demand.com" aria-label="Future Demand">
              <img className="fd-public__logo" src="/brand/FD-Logo-Blue.svg" alt="Future Demand" />
            </a>
            <div className="fd-public__tools">
              <div className="fd-public__badge">{copy.badge}</div>
              <div className="fd-segmented fd-segmented--sm" role="group" aria-label="Language">
                {(["en", "de"] as const).map((language) => (
                  <button
                    key={language}
                    type="button"
                    className={`fd-segmented__item${loginLanguage === language ? " fd-segmented__item--active" : ""}`}
                    aria-pressed={loginLanguage === language}
                    onClick={() => setLoginLanguage(language)}
                  >
                    {language.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>
        <main className="fd-public__content">
          <p className="fd-eyebrow">{copy.eyebrow}</p>
          <h1 className="fd-h1">{copy.title}</h1>
          <p className="fd-body fd-login-intro">{copy.intro}</p>

          {error && (
            <div className="fd-banner fd-banner--danger" role="alert">
              <strong>{copy.errorTitle}</strong>
              <span>{error}</span>
            </div>
          )}

          <section className="fd-card fd-card--elevated">
            <form onSubmit={(event) => void beginLogin(event)}>
              <div className="fd-field">
                <label className="fd-label" htmlFor="login-email">{copy.emailLabel}</label>
                <input
                  className="fd-input"
                  id="login-email"
                  type="email"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  required
                  autoFocus
                  placeholder={copy.emailPlaceholder}
                  autoComplete="email"
                />
                <span className="fd-hint">{copy.emailHint}</span>
              </div>
              <button
                className="fd-btn fd-btn--gold fd-btn--lg fd-login-submit"
                type="submit"
                disabled={actionPending("login") || !validEmail}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.75" />
                  <path d="M5 20.25c.35-4.05 2.72-6.25 7-6.25s6.65 2.2 7 6.25" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
                {actionPending("login") ? copy.continuing : copy.continue}
              </button>
            </form>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <img src="/brand/FD-Logo-Blue.svg" alt="Future Demand" />
          <span>Content Crawler</span>
        </div>
        <nav>
          <button className="nav-active">Jobs</button>
        </nav>
        <div className="sidebar-foot">
          <div><strong>{user.displayName || user.email || "Signed in"}</strong><span>{user.email}</span></div>
          <button
            className="text-button"
            disabled={actionPending("logout")}
            onClick={() => void beginLogout()}
          >
            {actionPending("logout") ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">Crawler workspace</p>
            <h1>Event import jobs</h1>
          </div>
          <div className="top-actions">
            {user.clients.length > 1 && (
              <label className="client-switcher">
                <span>Client</span>
                <select
                  aria-label="Select client account"
                  value={selectedClient}
                  onChange={(event) => setSelectedClient(event.target.value)}
                >
                  {user.clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name || client.id} · {client.role === "admin" ? "admin" : "member"}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {selectedDelegation && (
              <button
                type="button"
                className="button"
                disabled={actionPending(`delegation:${selectedDelegation.id}:revoke`)}
                onClick={() => void revokeScheduledAccess(selectedDelegation.id)}
              >
                {actionPending(`delegation:${selectedDelegation.id}:revoke`)
                  ? "Revoking…"
                  : "Revoke scheduled access"}
              </button>
            )}
            {selectedAccount?.canCreateJobs && (
              <button
                type="button"
                className="button button-gold"
                popoverTarget="create-job-popover"
                popoverTargetAction="show"
                onClick={() => {
                  setCreateError(null);
                  setCreditAcknowledged(false);
                }}
              >
                New job
              </button>
            )}
          </div>
        </header>
        {error && <div className="alert"><span>{error}</span><button onClick={() => setError(null)}>×</button></div>}
        {user.entitlementsMode === "bypass" && (
          <div className="alert alert-warning">
            Entitlement checks and credit accounting are temporarily bypassed for this deployment.
          </div>
        )}
        {showTermsAcceptanceNotice && (
          <div className="alert alert-warning terms-alert" role="alert">
            <span>
              Credit use is unavailable until an Owner or Billing Administrator accepts the new
              Future Demand Service Terms v4. {selectedAccount?.canManageBilling ? <>
                <a href={BILLING_URL} target="_blank" rel="noreferrer">Open Billing to review the terms</a>.
              </> : "Please contact an Owner or Billing Administrator in your organisation."}
            </span>
          </div>
        )}
        {creditsUnavailable && !showTermsAcceptanceNotice && (
          <div className="alert alert-warning credit-alert">
            <span>
              A run stopped because there were not enough available credits for that run.
              {selectedAccount?.canManageBilling ? <> <a href={BILLING_URL} target="_blank" rel="noreferrer">Manage credits in Billing</a>.</>
                : " Please contact an Owner or Billing Administrator in your organisation if you need more credits."}
            </span>
          </div>
        )}
        <section className="credit-guide" aria-labelledby="credit-guide-title">
          <div className="credit-guide-copy">
            <span className="credit-guide-icon" aria-hidden="true">C</span>
            <div>
              <h2 id="credit-guide-title">How crawler credits work</h2>
              <p>Every run compares each page with its last successful import before paid processing begins.</p>
            </div>
          </div>
          <div className="credit-guide-facts">
            <span><strong>{EVENT_CREDITS_PER_IMPORT} credits</strong> for each new or changed event successfully imported</span>
            <span><strong>Free</strong> when a page is unchanged or the import is unsuccessful</span>
            <span><strong>Scheduled jobs</strong> run automatically and can consume credits again whenever changes are successfully imported</span>
          </div>
          {selectedClient && <CreditBalance
            clientId={selectedClient}
            status={creditStatus?.clientId === selectedClient ? creditStatus.status : null}
            loading={creditStatus?.clientId !== selectedClient || creditStatus.loading}
            error={creditStatus?.clientId === selectedClient && creditStatus.error}
            canManageBilling={selectedAccount?.canManageBilling === true}
            onRefresh={() => setCreditRefresh((current) => current + 1)}
          />}
        </section>

        <>
            <section className="metric-grid">
              <article><span>Total jobs</span><strong>{visibleJobs.length}</strong></article>
              <article><span>Active</span><strong>{visibleJobs.filter((job) => job.status === "active").length}</strong></article>
              <article><span>Scheduled</span><strong>{visibleJobs.filter((job) => job.next_run_at !== null).length}</strong></article>
            </section>
            <section className="panel">
              <div className="panel-heading"><div><h2>Configured sources</h2><p>Total cost per run is {EVENT_CREDITS_PER_IMPORT} credits × successfully imported new or changed events.</p></div></div>
              {visibleJobs.length === 0 ? (
                <div className="empty"><h3>No crawler jobs yet</h3><p>Connect a single detail page or a collection URL to begin.</p></div>
              ) : (
                <div className="job-list">
                  <div className="job-list-header" aria-hidden="true">
                    <span>Source</span>
                    <span>Type</span>
                    <span>Schedule</span>
                    <span>Status</span>
                    <span>Actions</span>
                  </div>
                  {visibleJobs.map((job) => (
                    <article className="job-row" key={job.id}>
                      <button
                        className="job-main"
                        disabled={actionPending(`${job.id}:details`)}
                        onClick={() => void openJob(job.id)}
                      >
                        <span className={`kind-icon kind-${job.entity_kind}`}>{job.entity_kind[0]?.toUpperCase()}</span>
                        <span className="job-copy"><strong>{job.name}</strong><small>{job.seed_url}</small></span>
                      </button>
                      <span className="job-type">{job.entity_kind} · {job.crawl_mode}</span>
                      <span className="job-schedule">{job.cadence}<small>{job.next_run_at ? formatDate(job.next_run_at) : "No next run"}</small></span>
                      <Status value={job.status} className="job-status" />
                      <div className="row-actions">
                        {canManageJob(job) && job.status === "active" && (
                          <button
                            onClick={() => void jobAction(job, "run")}
                            disabled={!!job.active_run_id || actionPending(`${job.id}:run`)}
                          >
                            {actionPending(`${job.id}:run`) ? "Starting…" : "Run"}
                          </button>
                        )}
                        {canManageJob(job) && job.status === "completed" && job.cadence === "once" && (
                          <button
                            onClick={() => void jobAction(job, "run")}
                            disabled={!!job.active_run_id || actionPending(`${job.id}:run`)}
                          >
                            {actionPending(`${job.id}:run`) ? "Starting…" : "Run again"}
                          </button>
                        )}
                        {canManageJob(job) && job.status === "failed" && job.cadence === "once" && (
                          <button
                            className="action-primary"
                            onClick={() => void jobAction(job, "run")}
                            disabled={!!job.active_run_id || actionPending(`${job.id}:run`)}
                          >
                            {actionPending(`${job.id}:run`) ? "Starting…" : "Run again"}
                          </button>
                        )}
                        {canManageJob(job) && job.status === "authorization_required" && (
                          <button
                            className="action-primary"
                            onClick={() => void reauthorizeJob(job)}
                            disabled={actionPending(`${job.id}:reauthorize`)}
                          >
                            {actionPending(`${job.id}:reauthorize`) ? "Authorizing…" : "Authorize"}
                          </button>
                        )}
                        {canManageJob(job) && job.status === "active" && (
                          <button
                            onClick={() => void jobAction(job, "pause")}
                            disabled={actionPending(`${job.id}:pause`)}
                          >
                            {actionPending(`${job.id}:pause`) ? "Pausing…" : "Pause"}
                          </button>
                        )}
                        {canManageJob(job) && job.status === "paused" && (
                          <button
                            onClick={() => void jobAction(job, "resume")}
                            disabled={actionPending(`${job.id}:resume`)}
                          >
                            {actionPending(`${job.id}:resume`) ? "Resuming…" : "Resume"}
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
        </>
      </main>

      <form
        ref={createJobPopover}
        id="create-job-popover"
        className="modal"
        popover="auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-job-title"
        onSubmit={createJob}
      >
            <div className="modal-heading"><div><p className="eyebrow">New import source</p><h2 id="create-job-title">Configure crawler job</h2></div><button type="button" aria-label="Close" popoverTarget="create-job-popover" popoverTargetAction="hide" onClick={() => { setCreateError(null); setCreditAcknowledged(false); }}>×</button></div>
            {createError && <div className="alert modal-alert" role="alert">{createError}</div>}
            <label>Job name<input autoFocus required maxLength={120} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Summer event programme" /></label>
            <label>Seed URL<input required type="url" value={form.seedUrl} onChange={(event) => setForm({ ...form, seedUrl: event.target.value })} placeholder="https://example.com/events" /></label>
            <label>Source shape<select value={form.crawlMode} onChange={(event) => setForm({ ...form, crawlMode: event.target.value as typeof form.crawlMode })}><option value="single">Single event page</option><option value="collection">Event collection / programme</option></select></label>
            <div className="field-grid">
              <label>Frequency<select value={form.cadence} onChange={(event) => setForm({ ...form, cadence: event.target.value as typeof form.cadence })}><option value="once">One time</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></label>
              <label>Local run time<input type="time" value={form.localTime} onChange={(event) => setForm({ ...form, localTime: event.target.value })} /></label>
            </div>
            <label>Timezone<input required value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} /></label>
            {form.cadence === "weekly" && <label>Weekday<select value={form.weekday} onChange={(event) => setForm({ ...form, weekday: Number(event.target.value) })}>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day, index) => <option value={index + 1} key={day}>{day}</option>)}</select></label>}
            {form.cadence === "monthly" && <label>Day of month<input type="number" min="1" max="31" value={form.monthday} onChange={(event) => setForm({ ...form, monthday: Number(event.target.value) })} /><small>Short months use their final day.</small></label>}
            <div className="notice credit-notice" role="note">
              <strong>{cadenceLabels[form.cadence]} run and credit notice</strong>
              <p>
                The first run starts immediately.
                {form.cadence === "once"
                  ? " It runs once unless you or an administrator starts it again."
                  : ` It will then run automatically on its ${form.cadence} schedule until paused.`}
              </p>
              <ul>
                <li>Each new or changed event successfully imported into the client account costs {EVENT_CREDITS_PER_IMPORT} credits.</li>
                <li>A collection may import multiple events, so one run may consume multiple sets of {EVENT_CREDITS_PER_IMPORT} credits.</li>
                <li>Unchanged pages are skipped before AI extraction. Unchanged, failed, rejected, and partial imports consume no credits.</li>
                {form.cadence !== "once" && <li>Every scheduled run performs the same comparison and may consume credits again when changes are successfully imported.</li>}
              </ul>
            </div>
            <label className="credit-acknowledgement">
              <input
                type="checkbox"
                checked={creditAcknowledged}
                onChange={(event) => setCreditAcknowledged(event.target.checked)}
                required
              />
              <span>
                I understand the credit cost
                {form.cadence === "once" ? "." : " and that scheduled runs can consume credits automatically."}
              </span>
            </label>
            <div className="modal-actions"><button type="button" className="button" popoverTarget="create-job-popover" popoverTargetAction="hide" onClick={() => { setCreateError(null); setCreditAcknowledged(false); }}>Cancel</button><button type="submit" className="button button-gold" disabled={actionPending("create") || !creditAcknowledged}>{actionPending("create") ? "Creating…" : "Create and run"}</button></div>
      </form>

      {selectedJob && (
        <div className="drawer-backdrop" onMouseDown={() => setSelectedJob(null)}>
          <aside className="drawer" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-heading"><div><p className="eyebrow">Job detail</p><h2>{selectedJob.name}</h2></div><button onClick={() => setSelectedJob(null)}>×</button></div>
            {drawerError && <div className="alert" role="alert"><span>{drawerError}</span><button onClick={() => setDrawerError(null)}>×</button></div>}
            {drawerNotice && <div className="alert alert-info" role="status"><span>{drawerNotice}</span><button onClick={() => setDrawerNotice(null)}>×</button></div>}
            <dl className="detail-grid"><div><dt>Status</dt><dd><Status value={selectedJob.status} /></dd></div><div><dt>Source</dt><dd>{selectedJob.entity_kind} · {selectedJob.crawl_mode}</dd></div><div><dt>Schedule</dt><dd>{selectedJob.cadence} at {selectedJob.local_time}</dd></div><div><dt>Next run</dt><dd>{formatDate(selectedJob.next_run_at)}</dd></div></dl>
            {selectedJob.authorization_error && (
              <div className="alert alert-warning">{selectedJob.authorization_error}</div>
            )}
            <div className="job-credit-note">
              <strong>{selectedJob.cadence === "once" ? "Credit use for this job" : `${cadenceLabels[selectedJob.cadence]} scheduled credit use`}</strong>
              <span>
                {selectedJob.cadence === "once"
                  ? "A manual rerun can consume credits again when new or changed events are successfully imported."
                  : `This job runs automatically ${selectedJob.cadence} and can consume credits on each run when new or changed events are successfully imported.`}
                {" "}Unchanged and unsuccessful imports are free.
              </span>
              <a href={BILLING_URL} target="_blank" rel="noreferrer">View credit status</a>
            </div>
            <a className="source-link" href={selectedJob.seed_url} target="_blank" rel="noreferrer">{selectedJob.seed_url}</a>
            <div className="drawer-heading">
              <h3>Run history</h3>
              <div className="drawer-actions">
                {canManageJob(selectedJob) && selectedJob.cadence === "once" && ["completed", "failed"].includes(selectedJob.status) && (
                  <button
                    className="action-primary"
                    onClick={() => void jobAction(selectedJob, "run")}
                    disabled={!!selectedJob.active_run_id || actionPending(`${selectedJob.id}:run`)}
                  >
                    {actionPending(`${selectedJob.id}:run`) ? "Starting…" : "Run again"}
                  </button>
                )}
                {canManageJob(selectedJob) && ["active", "completed"].includes(selectedJob.status) && <button onClick={() => void jobAction(selectedJob, "refresh")} disabled={Boolean(selectedJob.active_run_id) || actionPending(`${selectedJob.id}:refresh`)}>{actionPending(`${selectedJob.id}:refresh`) ? "Starting refresh…" : "Refresh event details"}</button>}
                {canManageJob(selectedJob) && selectedJob.status === "authorization_required" && (
                  <button
                    className="action-primary"
                    onClick={() => void reauthorizeJob(selectedJob)}
                    disabled={actionPending(`${selectedJob.id}:reauthorize`)}
                  >
                    {actionPending(`${selectedJob.id}:reauthorize`) ? "Authorizing…" : "Authorize scheduled access"}
                  </button>
                )}
                {canManageJob(selectedJob) && selectedJob.status === "active" && (
                  <button
                    onClick={() => void jobAction(selectedJob, "preview")}
                    disabled={!!selectedJob.active_run_id || actionPending(`${selectedJob.id}:preview`)}
                  >
                    {actionPending(`${selectedJob.id}:preview`) ? "Preparing…" : "Preview URLs"}
                  </button>
                )}
              </div>
            </div>
            <div className="run-list">
              {runs.map((run) => (
                <article className={run.status === "failed" ? "run-failed" : ""} key={run.id}>
                  <div><strong>{run.retry_of_run_id ? "retry run" : `${run.trigger_type} run`}</strong><small>{formatDate(run.created_at)}</small></div>
                  <Status value={run.status} hasFailures={run.failed_count > 0} />
                  <div className="run-counts"><span>{run.discovered_count} candidate pages</span><strong>Total cost: {run.credits_settled} credits</strong><span>{run.succeeded_count} event pages imported</span>{run.unchanged_count > 0 && <span>{run.unchanged_count} unchanged · free</span>}{run.rejected_count > 0 && <span>{run.rejected_count} skipped as non-events</span>}{run.failed_count > 0 && <span>{run.failed_count} failed</span>}{run.removed_count > 0 && <span>{run.removed_count} removed</span>}</div>
                  <div className="run-stages">
                    {run.stages.map((stage, index) => {
                      const done = Math.min(stage.total, stage.completed + stage.failed + stage.skipped);
                      const progress = stage.total > 0 ? Math.round((done / stage.total) * 100) : 0;
                      return (
                        <div className="run-stage" key={stage.key}>
                          <span className="run-stage-index">{index + 1}</span>
                          <div className="run-stage-copy">
                            <div className="run-stage-heading">
                              <strong>{stage.label}</strong>
                              <div>
                                <Status value={stage.status} />
                                {selectedAccount?.role === "admin" && (
                                  <button
                                    type="button"
                                    className="stage-data-button"
                                    onClick={() => void openStageData(run, stage.key)}
                                  >
                                    View data
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="run-stage-progress" aria-label={`${stage.label}: ${progress}%`}>
                              <span style={{ width: `${progress}%` }} />
                            </div>
                            <small>
                              {stage.completed} of {stage.total} pages completed
                              {stage.reused > 0 && ` · ${stage.reused} reused`}
                              {stage.skipped > 0 && ` · ${stage.skipped} skipped`}
                              {stage.failed > 0 && ` · ${stage.failed} failed`}
                            </small>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(run.errors.length > 0 || run.error_message) && (
                    <div className="run-error">
                      <strong>{run.status === "failed" ? "Why this run failed" : "Processing issues"}</strong>
                      {run.errors.length > 0
                        ? run.errors.map((item) => <span key={`${item.code}:${item.message}`}>{item.count} × {item.message}</span>)
                        : <span>{run.error_message}</span>}
                    </div>
                  )}
                  <div className="run-actions">
                    {canManageJob(selectedJob) && <button type="button" className="action-primary" onClick={() => setResultsRun(run)}>View results</button>}
                    <button
                      type="button"
                      onClick={() => void downloadRunSummary(run)}
                      disabled={actionPending(`${run.id}:summary`)}
                    >
                      {actionPending(`${run.id}:summary`) ? "Preparing summary…" : "Download summary"}
                    </button>
                    {(run.failed_count > 0 || run.status === "cancelled") && ["completed", "failed", "cancelled"].includes(run.status) && canManageJob(selectedJob) && (
                      <button
                        className="action-primary"
                        onClick={() => void retryFailedSteps(run)}
                        disabled={!!selectedJob.active_run_id || actionPending(`${run.id}:retry`)}
                      >
                        {actionPending(`${run.id}:retry`) ? "Starting retry…" : "Retry failed steps"}
                      </button>
                    )}
                    {canManageJob(selectedJob) && !["completed", "failed", "cancelled", "insufficient_credits", "skipped_overlap"].includes(run.status) && (
                      <button
                        className="action-danger"
                        onClick={() => void stopRun(run)}
                        disabled={actionPending(`${run.id}:stop`)}
                      >
                        {actionPending(`${run.id}:stop`) ? "Stopping…" : "Stop run"}
                      </button>
                    )}
                  </div>
                </article>
              ))}
              {runs.length === 0 && <p className="muted">No runs recorded yet.</p>}
            </div>
          </aside>
        </div>
      )}
      {resultsRun && selectedJob && <RunResultsDialog runId={resultsRun.id} onClose={() => setResultsRun(null)} onRetry={() => retryFailedSteps(resultsRun)} retryDisabled={Boolean(selectedJob.active_run_id)} />}
      {inspection && (
        <div className="inspector-backdrop" onMouseDown={() => setInspection(null)}>
          <section className="inspector" onMouseDown={(event) => event.stopPropagation()} aria-label={stageTitles[inspection.stage]}>
            <header className="inspector-header">
              <div>
                <p className="eyebrow">Run step data</p>
                <h2>{stageTitles[inspection.stage]}</h2>
                <p>Raw crawl and AI artifacts are retained for {inspection.artifactRetentionDays} days.</p>
              </div>
              <button type="button" aria-label="Close data inspector" onClick={() => setInspection(null)}>×</button>
            </header>
            <div className="inspector-body">
              <aside className="inspector-list">
                {inspection.stage === "discovery" && (
                  <>
                    <h3>Collection pages</h3>
                    {inspection.discoveryPages.map((page) => (
                      <button
                        type="button"
                        className={inspectionSelection === `page:${page.page}` ? "selected" : ""}
                        key={page.page}
                        onClick={() => void loadDiscoveryPage(inspection.run.id, page.page)}
                      >
                        <strong>Page {page.page}</strong>
                        <span>{page.title || page.finalUrl || "Retained collection page"}</span>
                        <small>{page.linkCount} links</small>
                      </button>
                    ))}
                    <h3>Discovered event pages</h3>
                  </>
                )}
                {inspection.items.map((item) => (
                  <button
                    type="button"
                    className={inspectionSelection === item.id ? "selected" : ""}
                    key={item.id}
                    onClick={() => void loadInspectionItem(inspection.run.id, item.id)}
                  >
                    <strong>Page {item.occurrenceOrder}</strong>
                    <span>{item.sourceUrl}</span>
                    <Status value={item.status} />
                  </button>
                ))}
                {inspection.items.length === 0 && inspection.discoveryPages.length === 0 && (
                  <p className="muted">No retained records for this step.</p>
                )}
              </aside>
              <main className="inspector-content">
                {inspectionLoading && <div className="inspector-loading"><div className="loader" /><span>Loading retained data…</span></div>}
                {!inspectionLoading && inspectionDiscovery != null && (
                  <section>
                    <h3>Rendered collection-page evidence</h3>
                    <pre>{prettyJson(inspectionDiscovery)}</pre>
                  </section>
                )}
                {!inspectionLoading && inspectionItem && (
                  <>
                    <div className="inspector-item-heading">
                      <div>
                        <h3>{inspectionItem.item.sourceUrl}</h3>
                        <p>{inspectionItem.item.errorMessage || inspectionItem.item.clientResultStatus || inspectionItem.item.status}</p>
                      </div>
                      <a href={inspectionItem.item.sourceUrl} target="_blank" rel="noreferrer">Open source</a>
                    </div>
                    {inspection.stage === "discovery" && (
                      <section><h3>Discovered page record</h3><pre>{prettyJson(inspectionItem.item)}</pre></section>
                    )}
                    {inspection.stage === "scrape" && (
                      <section><h3>Page evidence sent to extraction</h3><pre>{prettyJson(inspectionItem.scrapeEvidence)}</pre></section>
                    )}
                    {inspection.stage === "structure" && (
                      <>
                        <section><h3>Usable Future Demand event structure</h3><pre>{prettyJson(inspectionItem.structuredData)}</pre></section>
                        <section><h3>Exact AI request and raw response</h3><pre>{prettyJson(inspectionItem.extractionTrace)}</pre></section>
                      </>
                    )}
                    {inspection.stage === "ingest" && (
                      <>
                        <section><h3>Per-occurrence results</h3><pre>{prettyJson(inspectionItem.importResults)}</pre></section>
                        <section><h3>Client lookup and Domain API attempts</h3><pre>{prettyJson(inspectionItem.importAttempts)}</pre></section>
                      </>
                    )}
                  </>
                )}
                {!inspectionLoading && inspectionDiscovery == null && !inspectionItem && (
                  <p className="muted">Choose a page to inspect its retained data.</p>
                )}
              </main>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
